/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  a11y handlers for keyboard events
 */

import {
  a11yButtonHandler as _a11yButtonHandler,
  a11yLinkHandler as _a11yLinkHandler,
} from '@zcode/a11y-utils'
import React from 'react'

// https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_button_role#Keyboard_and_focus
// Quote: Buttons are expected to be triggered using the Space or Enter key,
// while links are expected to be triggered using [only] the Enter key.

// http://www.karlgroves.com/2014/11/24/ridiculously-easy-trick-for-keyboard-accessibility/
// <div onClick={onClick} onKeyPress={a11yButtonHandler(onClick)} role="button" />
const buttonHandlerCache = new WeakMap<
  ((event: React.KeyboardEvent<unknown>) => void) | ((event: KeyboardEvent) => void),
  ((event: React.KeyboardEvent<any>) => void) | ((event: KeyboardEvent) => void)
>()
export function a11yButtonHandler<
  T extends ((event: React.KeyboardEvent<any>) => void) | ((event: KeyboardEvent) => void),
>(handler: T): T {
  let wrappedHandler = buttonHandlerCache.get(handler)
  if (!wrappedHandler) {
    wrappedHandler = (event: Parameters<T>[0]) => {
      if (event.target !== event.currentTarget) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      // @ts-expect-error TS isn't sure if we have a react or native handler/event
      handler(event)
    }
    buttonHandlerCache.set(handler, wrappedHandler)
  }
  // @ts-expect-error TS isn't sure if we have a react or native handler
  return _a11yButtonHandler(wrappedHandler)
}

// <div onClick={onClick} onKeyPress={a11yLinkHandler(onClick)} role="link" />
const linkHandlerCache = new WeakMap<
  ((event: React.KeyboardEvent<unknown>) => void) | ((event: KeyboardEvent) => void),
  ((event: React.KeyboardEvent<any>) => void) | ((event: KeyboardEvent) => void)
>()
export function a11yLinkHandler<
  T extends ((event: React.KeyboardEvent<any>) => void) | ((event: KeyboardEvent) => void),
>(handler: T): T {
  let wrappedHandler = linkHandlerCache.get(handler)
  if (!wrappedHandler) {
    wrappedHandler = (event: Parameters<T>[0]) => {
      if (event.target !== event.currentTarget) {
        return
      }
      event.stopPropagation()
      // @ts-expect-error TS isn't sure if we have a react or native handler/event
      handler(event)
    }
    linkHandlerCache.set(handler, wrappedHandler)
  }
  // @ts-expect-error TS isn't sure if we have a react or native handler
  return _a11yLinkHandler(wrappedHandler)
}
